import { useRef, useState } from 'react';
import { FFT_LOG_BINS, THIRD_OCTAVE_CENTRES, fftLogBinFrequency } from '@shared/protocol';
import { getPalette, useCanvas } from '../../lib/canvas';
import type { LiveRefs } from '../../lib/useStreamLive';
import { Panel, Segmented } from '../Panel';

type Mode = 'third' | 'fft';
const MIN_DB = -90;
const MAX_DB = 0;
const HOLD_MS = 2000;
const FALL_DB_PER_S = 20;

const LABELS: [number, string][] = [
  [31.5, '31'],
  [63, '63'],
  [125, '125'],
  [250, '250'],
  [500, '500'],
  [1000, '1k'],
  [2000, '2k'],
  [4000, '4k'],
  [8000, '8k'],
  [16000, '16k'],
];

const xOfFreq = (f: number, left: number, width: number) => left + (Math.log10(f / 20) / Math.log10(1000)) * width;

/** Spectrum analyser for one channel group: 1/3-octave bars or log-spaced FFT, with peak hold. */
export function SpectrumPanel({ live, groupId, groupName }: { live: React.RefObject<LiveRefs>; groupId: string | null; groupName: string | null }) {
  const [mode, setMode] = useState<Mode>('third');
  const shown = useRef<number[]>([]);
  const holds = useRef<{ v: number; until: number }[]>([]);
  const last = useRef(0);
  const modeRef = useRef(mode);
  if (modeRef.current !== mode) {
    modeRef.current = mode;
    shown.current = [];
    holds.current = [];
  }

  const ref = useCanvas((ctx, w, h, now) => {
    const p = getPalette();
    const spec = live.current?.spectrum;
    const g = spec?.groups.find((x) => x.id === groupId) ?? spec?.groups[0];
    const values = g ? (mode === 'third' ? g.thirdOctave : g.fft) : [];
    const n = mode === 'third' ? THIRD_OCTAVE_CENTRES.length : FFT_LOG_BINS;
    const dt = last.current ? Math.min(0.25, (now - last.current) / 1000) : 0;
    last.current = now;
    for (let i = 0; i < n; i++) {
      const target = values[i] ?? MIN_DB;
      const cur = shown.current[i] ?? MIN_DB;
      shown.current[i] = target >= cur ? target : Math.max(target, cur - FALL_DB_PER_S * dt);
      const hold = holds.current[i] ?? { v: MIN_DB, until: 0 };
      if (target >= hold.v || now > hold.until) holds.current[i] = target >= hold.v ? { v: target, until: now + HOLD_MS } : { v: Math.max(shown.current[i], hold.v - FALL_DB_PER_S * dt), until: hold.until };
    }

    const left = 34;
    const right = w - 6;
    const top = 6;
    const bottom = h - 18;
    const width = right - left;
    const yOf = (db: number) => bottom - ((Math.max(MIN_DB, Math.min(MAX_DB, db)) - MIN_DB) / (MAX_DB - MIN_DB)) * (bottom - top);
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = p.scopeBg;
    ctx.fillRect(left, top, width, bottom - top);
    ctx.strokeStyle = p.scopeGrid;
    ctx.fillStyle = p.scale;
    ctx.font = `11px ${p.font}`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let db = MAX_DB; db >= MIN_DB; db -= 15) {
      const y = Math.round(yOf(db)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
      ctx.stroke();
      ctx.fillText(String(db), left - 5, y);
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (const [f, label] of LABELS) {
      const x = Math.round(xOfFreq(f, left, width)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.lineTo(x, bottom);
      ctx.stroke();
      ctx.fillText(label, x, bottom + 4);
    }

    if (mode === 'third') {
      const bw = (width / n) * 0.78;
      for (let i = 0; i < n; i++) {
        const x = xOfFreq(THIRD_OCTAVE_CENTRES[i], left, width) - bw / 2;
        const y = yOf(shown.current[i]);
        ctx.fillStyle = p.graphShort;
        ctx.globalAlpha = 0.85;
        ctx.fillRect(x, y, bw, bottom - y);
        ctx.globalAlpha = 1;
        const hy = Math.round(yOf(holds.current[i]?.v ?? MIN_DB));
        ctx.fillStyle = p.hold;
        if (hy < bottom - 1) ctx.fillRect(x, hy - 1, bw, 2);
      }
    } else {
      const line = (arr: number[], color: string, width_: number) => {
        ctx.strokeStyle = color;
        ctx.lineWidth = width_;
        ctx.beginPath();
        for (let i = 0; i < n; i++) {
          const x = xOfFreq(fftLogBinFrequency(i), left, width);
          const y = yOf(arr[i] ?? MIN_DB);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.lineWidth = 1;
      };
      line(holds.current.map((x) => x?.v ?? MIN_DB), p.graphMomentary, 1);
      line(shown.current, p.graphShort, 1.5);
    }
  });

  return (
    <Panel
      title={`Spectrum${groupName ? `: ${groupName}` : ''}`}
      actions={
        <Segmented<Mode>
          label="Spectrum display"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'third', label: '1/3 octave' },
            { value: 'fft', label: 'FFT' },
          ]}
        />
      }
    >
      <canvas ref={ref} className="spectrum-canvas" role="img" aria-label="Audio spectrum, 20 Hz to 20 kHz" />
      <p className="muted small">Level in dBFS (a full-scale sine reads 0 dB). Thin line or marker: peak hold for 2 s.</p>
    </Panel>
  );
}
